import React, { useEffect, useState, useRef } from 'react';
import { fixHtmlImagePaths, processEmbedUrls } from "../utils/imageUtils";
import { useParams, Link } from 'react-router-dom';
import { getServiceItemById } from '../services/ServiceService';
import { normalizeImageUrl } from '../utils/imageUtils';
import { useData } from '../context/DataContext';
import { updateMetaTags } from '../utils/seoUtils';
import useTikTokEmbed from '../utils/useTikTokEmbed';
import ReviewSection from '../components/ReviewSection';

export default function ServiceDetailPage() {
    const { slug } = useParams();
    const { serviceGroups, serviceItems, loading: globalLoading, settings } = useData();
    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);
    const contentRef = useRef(null);

    useTikTokEmbed(contentRef, [service]);
    const [relatedServices, setRelatedServices] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [groupSlug, setGroupSlug] = useState("");

    const lastFetchedSlugRef = useRef(null);
    const isFetchingRef = useRef(false);

    useEffect(() => {
        if (service) {
            updateMetaTags({
                title: service.title || service.name,
                description: service.shortDescription || service.description || service.title || service.name,
                image: service.imageUrl,
            });
        }
    }, [service]);

    useEffect(() => {
        const fetchServiceDetail = async () => {
            if (globalLoading) return;
            if (isFetchingRef.current) return;
            if (lastFetchedSlugRef.current === slug) return;

            isFetchingRef.current = true;
            setLoading(true);
            try {
                const summary = serviceItems.find(item => item.slug === slug);

                if (summary) {
                    const id = summary.id || summary._id;
                    const fullDetail = await getServiceItemById(id);
                    setService(fullDetail || summary);
                    lastFetchedSlugRef.current = slug;

                    const currentItem = fullDetail || summary;

                    const sameGroup = serviceItems.filter(item =>
                        item.serviceGroupId === currentItem.serviceGroupId && item.slug !== slug
                    ).slice(0, 5);
                    setRelatedServices(sameGroup);

                    const group = serviceGroups.find(g => (g._id || g.id) === currentItem.serviceGroupId);
                    if (group) {
                        setGroupName(group.name);
                        setGroupSlug(group.slug);
                    }
                } else {
                    setService(null);
                }
            } catch (error) {
                console.error("Error fetching service detail:", error);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        };

        fetchServiceDetail();
        window.scrollTo(0, 0);
    }, [slug, globalLoading, serviceItems, serviceGroups]);

    if (loading) {
        return <div className="text-center py-5">Đang tải dữ liệu...</div>;
    }

    if (!service) {
        return (
            <div className="text-center py-5">
                <h2>Không tìm thấy dịch vụ</h2>
                <Link to="/dich-vu" className="btn btn-primary mt-3" style={{ background: '#ff0000', border: 'none' }}>Quay lại danh sách dịch vụ</Link>
            </div>
        );
    }

    return (
        <main style={{ backgroundColor: '#f4f7f6', color: '#333', paddingBottom: '100px' }}>
            <div className="py-4 mt-[50px] mb-4">
                <div className="container px-4 text-left">
                    <nav aria-label="breadcrumb">
                        <ol className="flex items-center gap-2 text-xs md:text-sm uppercase tracking-wider mb-0 p-0 list-none">
                            <li className="breadcrumb-item"><Link to="/" className="hover:text-red-600 transition-colors no-underline text-slate-500">Trang chủ</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="breadcrumb-item"><Link to="/dich-vu" className="hover:text-red-600 transition-colors no-underline text-slate-500">Dịch vụ</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="breadcrumb-item active text-slate-900 font-bold" aria-current="page">{service.title || service.name}</li>
                        </ol>
                    </nav>
                </div>
            </div>

            <div className="container">
                <div className="row" style={{ display: 'flex', alignItems: 'flex-start' }}>

                    <div className="col-lg-9 col-md-12 mb-4">
                        <div style={{
                            backgroundColor: '#fff',
                            padding: '30px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%'
                        }}>
                            <h1 style={{
                                fontSize: '26px',
                                fontWeight: '800',
                                marginBottom: '20px',
                                color: '#ff0000',
                                lineHeight: '1.4',
                                wordBreak: 'break-word'
                            }}>
                                {service.title || service.name}
                            </h1>

                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '15px',
                                color: '#666',
                                fontSize: '14px',
                                borderBottom: '1px solid #f0f0f0',
                                paddingBottom: '15px',
                                marginBottom: '25px'
                            }}>
                                <span className="d-flex align-items-center gap-1">
                                    <i className="fa fa-folder-open text-danger"></i> Nhóm: {groupName || service.serviceGroupName || 'Chưa phân nhóm'}
                                </span>
                                {service.price && service.price > 0 && (
                                    <span className="d-flex align-items-center gap-1">
                                        <i className="fa fa-money text-danger"></i> Giá từ: <strong style={{ color: '#ff0000', fontSize: '16px' }}>{service.price.toLocaleString('vi-VN')} VNĐ</strong>
                                    </span>
                                )}
                            </div>

                            <img
                                src={normalizeImageUrl(service.imageUrl)}
                                alt={service.title}
                                style={{ width: '100%', borderRadius: 8, marginBottom: 30, objectFit: 'cover', maxHeight: 450, boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
                            />

                            <div className="job-content ql-editor" style={{
                                fontSize: '16px',
                                lineHeight: '1.8',
                                color: '#444',
                                overflow: 'hidden',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                            }}>
                                {service.content ? (
                                    <div
                                        ref={contentRef}
                                        className="service-content-inner"
                                        style={{ maxWidth: '100%', wordBreak: 'normal', overflowWrap: 'break-word', wordWrap: 'break-word' }}
                                        dangerouslySetInnerHTML={{ __html: processEmbedUrls(fixHtmlImagePaths(service.content.replace(/&nbsp;/g, ' '))) }}
                                    />
                                ) : (
                                    <p>Nội dung chi tiết dịch vụ đang được cập nhật...</p>
                                )}
                            </div>

                            <div style={{
                                marginTop: '40px',
                                padding: '30px',
                                background: '#f8f9fa',
                                borderRadius: '12px',
                                border: '1px solid #e9ecef',
                                textAlign: 'center'
                            }}>
                                <h4 style={{ fontWeight: 'bold', marginBottom: '15px', color: '#333' }}>Cần tư vấn ngay về dịch vụ này?</h4>
                                <p style={{ color: '#666', marginBottom: '20px' }}>Liên hệ với chúng tôi để được tư vấn và báo giá chi tiết cho vấn đề xe của bạn.</p>
                                <div className="d-flex justify-content-center gap-3 flex-wrap">
                                    <Link to="/dat-lich-hen" className="btn btn-primary btn-lg rounded-pill px-5 fw-bold shadow-sm btn-blink-orange">
                                        Đặt lịch hẹn ngay
                                    </Link>
                                    <a href={`tel:${settings?.hotline?.replace(/[^0-9]/g, '')}`} className="btn btn-outline-dark btn-lg rounded-pill px-5 fw-bold" style={{ border: '2px solid #333' }}>
                                        {settings?.hotline}
                                    </a>
                                </div>
                            </div>

                            {/* Review & Rating Section */}
                            <ReviewSection
                                targetType="service"
                                targetId={slug || service?._id}
                                targetTitle={service?.title}
                            />
                        </div>
                    </div>

                    <div className="col-lg-3 d-none d-lg-block" style={{ position: 'sticky', top: '100px' }}>
                        <div style={{ width: '100%' }}>
                            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '25px' }}>
                                <h5 style={{ fontSize: '16px', fontWeight: 'bold', borderBottom: '2px solid #ff0000', paddingBottom: '10px', marginBottom: '20px' }}>THÔNG TIN DỊCH VỤ</h5>

                                <div className="mb-3">
                                    <small style={{ color: '#999', fontSize: '11px', fontWeight: 'bold', display: 'block', textTransform: 'uppercase' }}>Danh mục</small>
                                    <span style={{ color: '#d00', fontWeight: 'bold', fontSize: '15px' }}>
                                        {groupName || service.serviceGroupName || 'Dịch vụ'}
                                    </span>
                                </div>

                                <div className="mb-0">
                                    <small style={{ color: '#999', fontSize: '11px', fontWeight: 'bold', display: 'block', textTransform: 'uppercase' }}>Giá tham khảo</small>
                                    <span style={{ fontWeight: '600', color: '#ff0000', fontSize: '16px' }}>
                                        {service.price ? `${service.price.toLocaleString('vi-VN')} đ` : 'Liên hệ báo giá'}
                                    </span>
                                </div>
                            </div>

                            {relatedServices.length > 0 && (
                                <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                    <h5 style={{ fontSize: '16px', fontWeight: 'bold', borderBottom: '2px solid #ddd', paddingBottom: '10px', marginBottom: '20px' }}>DỊCH VỤ LIÊN QUAN</h5>

                                    {relatedServices.slice(0, 4).map((rs, idx) => (
                                        <div key={idx} style={{ paddingBottom: '15px', marginBottom: '15px', borderBottom: idx < 3 ? '1px dashed #eee' : 'none' }}>
                                            <Link to={`/dich-vu/${rs.slug}`} style={{ color: '#333', textDecoration: 'none', fontWeight: '700', fontSize: '14px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                <img
                                                    src={normalizeImageUrl(rs.imageUrl)}
                                                    alt={rs.title}
                                                    style={{ width: '60px', height: '45px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }}
                                                />
                                                <div style={{ lineHeight: '1.4' }}>
                                                    {rs.title}
                                                    {rs.price > 0 && <div style={{ color: '#ff0000', fontSize: '12px', marginTop: '4px' }}>{rs.price.toLocaleString('vi-VN')} đ</div>}
                                                </div>
                                            </Link>
                                        </div>
                                    ))}

                                    <Link to={`/dich-vu-nhom/${groupSlug || 'tat-ca'}`} className="btn btn-light w-100 fw-bold border" style={{ fontSize: '13px' }}>
                                        Xem thêm <i className="fa fa-angle-right ms-1"></i>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .job-content img { max-width: 100% !important; height: auto !important; border-radius: 8px; margin: 15px 0; }
                .job-content iframe { max-width: 100% !important; }
                .job-content table { width: 100% !important; overflow-wrap: break-word; }
                .job-content * { 
                    word-break: normal !important; 
                    overflow-wrap: break-word !important; 
                    word-wrap: break-word !important; 
                }
                .job-content p { margin-bottom: 1rem; }
                .job-content h2, .job-content h3 { margin-top: 1.5rem; margin-bottom: 1rem; font-weight: bold; color: #333; }
                
                @keyframes blink-orange {
                    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7); }
                    50% { transform: scale(1.05); box-shadow: 0 0 15px 5px rgba(255, 0, 0, 0.4); }
                    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 0, 0, 0); }
                }
                
                .btn-blink-orange {
                    background-color: #ff0000 !important;
                    border-color: #ff0000 !important;
                    color: #fff !important;
                    animation: blink-orange 1.5s infinite ease-in-out;
                    transition: all 0.3s ease;
                }
                
                .btn-blink-orange:hover {
                    background-color: #e57300 !important;
                    transform: scale(1.1) !important;
                    box-shadow: 0 0 20px rgba(255, 0, 0, 0.6) !important;
                }

                @media (max-width: 991px) {
                    .col-lg-3 { position: static !important; }
                }
            `}</style>
        </main>
    );
}
